import {Map as OLMap} from 'ol/index';
import {
  Fill,
  Style,
  Text,
  Icon,
  Stroke
} from 'ol/style';
import {Cluster, Vector as VectorSource} from 'ol/source';
import {Vector as VectorLayer} from 'ol/layer';
import {Point} from 'ol/geom';
import {
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  MathUtils,
  Mesh,
  Scene,
  Vector3
} from "three";
import {DataProjectionCoordinate} from "./projections";
import {TagMaterial} from "./rendering";
import {Viewer} from "./main";
import GPUPicker from "./gpu-picker";
import { TagFeature } from './aerial';
import { isEqual } from 'lodash';
import { getTagTexturePath } from '../textures/tag-icons';
import { Asset, TagData, TagItemData } from '../../redux/assets-slice';
import { clearAerialTooltip, clearViewerTooltip, setAerialTooltip, setViewerTooltip } from '../viewer';
import { mergeBoundingBoxes } from './utilities';
import { t } from '../../localization';
import { TagSize } from '../../types/tags';
import { showTagDetails } from '../../folder';
import { SceneCameraState } from '../../redux/camera-slice';

const tagIconSize = 24;

export class TagMesh extends Mesh {
  public tags;

  constructor(geometry, material, tags) {
    super(geometry, material);
    this.tags = tags;
  };
};

class TagPosition {
  private _value;
  private tag: TagItem;

  constructor(tag: TagItem, position) {
    this.tag = tag;
    this._value = new Vector3(...position);
  }

  get array() {
    return this.tag.isAerial
      ? [this._value.x, this._value.y]
      : [this._value.x, this._value.y, this._value.z];
  }

  get viewer() {
    return new DataProjectionCoordinate(this._value).toScene();
  }

  get aerial() {
    return new DataProjectionCoordinate(this._value).toAerial();
  }
}

export class TagItem {
  public id: string;
  public csv: string;
  public name: string;
  public file: TagFile;
  public position: TagPosition;
  public comment: string;
  public sceneState: SceneCameraState;
  public isAerial = false;
  public texture: string;
  public visible: boolean;
  public size: number;

  constructor(asset: TagFile, tag: TagItemData) {
    this.id = tag.id;
    this.file = asset;
    this.name = tag.name;
    this.comment = tag.comment;
    this.sceneState = tag.sceneState;
    this.isAerial = !("z" in tag);

    this.csv = asset.id;
    this.texture = asset.texture;
    this.visible = asset.visible;
    this.size = asset.size;

    const position = [tag.x, tag.y, tag.z];
    this.position = new TagPosition(this, position);
  }
}

class TagFile {
  public id: string;
  public saved: boolean;
  public size: TagSize;
  public texture: string;
  public visible: boolean;
  public name: string;
  public tags: TagItem[] = [];

  constructor(asset: Asset) {
    this.id = asset.id;
    this.saved = asset.saved;
    this.name = asset.name;
  }

  update(asset: Asset) {
    const {size, texture, items} = asset.data as TagData;

    const visibleChanged = this.visible !== asset.visible;
    this.visible = asset.visible;

    const textureChanged = this.texture !== texture;
    this.texture = texture;

    const sizeChanged = this.size !== size;
    this.size = size;

    const newTagIDs = items.map(x => x.id);
    const currentTagIDs = this.tags.map(x => x.id);
    const tagsChanged = !isEqual(newTagIDs, currentTagIDs);

    // Update all tags (comments may have changed)
    this.tags = items.map(tag => {
      return new TagItem(this, tag);
    });

    return visibleChanged
      || textureChanged
      || tagsChanged
      || sizeChanged;
  }
}

export default class TagController {
  public viewer: Viewer;
  public sceneViewController: SceneViewTags;
  public aerialController: AerialTags;
  public tagState = {};
  private files: TagFile[] = [];

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.sceneViewController = new SceneViewTags(this);
    this.aerialController = new AerialTags(this);
  }

  get hovering() {
    return this.aerialController.hovering;
  }

  get scene(): Scene {
    return this.sceneViewController.scene;
  }

  get allTags(): TagItem[] {
    return this.files.reduce((previous, current) =>
      [...previous, ...current.tags], []);
  }

  get combinedBoundingBox() {
    return this.boundingBoxForTags(this.allTags);
  }

  public get numberOfFiles() {
    return this.files.length;
  }

  addNewFile(asset) {
    let assetID = asset.id;
    if (this.getByID(assetID)) {
      return;
    }

    const file = new TagFile(asset);
    this.files.push(file);
  }

  deleteFile(assetID) {
    let file = this.getByID(assetID);
    if (!file) {
      return;
    }

    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
  }

  updateTagData = (assetID, asset) => {
    let file = this.getByID(assetID);
    if (!file) return false;

    return file.update(asset);
  }

  getTooltipText(tagID): string[] {
    const selected = this.allTags.find(x => x.id === tagID);
    if (!selected) return;

    const fileName = selected.file.name;
    const tagName = selected.name;

    return [`[${fileName}]`, tagName];
  }

  getByID(assetID) {
    return this.files.find(file => file.id === assetID);
  }

  getTagByID(tagID) {
    return this.files
      .map(file => file.tags)
      .flat()
      .find(tag => tag.id === tagID);
  }

  getRowsCSV(tags: TagItem[]) {
    let tagCsvOut = [];

    const hasHeight = tags.filter(tag => !tag.isAerial).length > 0;

    const header = [
      ...['Name', 'Easting', 'Northing'],
      ...hasHeight ? ['Elevation'] : [],
      'Comments'
    ];

    tagCsvOut.push(header);

    tags.forEach(tag => {
      const row = [
        tag.name,
        ...tag.position.array,
        ...tag.isAerial ? [''] : [],
        tag.comment
      ];
      tagCsvOut.push(row);
    });

    return tagCsvOut;
  }

  setMaxDistance(distance: number) {
    let value = (distance > 999) ? 1e10 : distance;
    this.sceneViewController.setMaxDistance(value);
  }

  reconcile(tagFiles) {
    const currentFiles = new Set(this.files.map(x => x.id));
    const newFiles = new Set(tagFiles.map(x => x.id));
    const filesToLoad = tagFiles.filter(x => !currentFiles.has(x.id));
    const filesToDelete = this.files.filter(x => !newFiles.has(x.id));

    // Delete files that need to be removed
    filesToDelete.forEach(file => {
      this.deleteFile(file.id);
    });

    // Add new tag files
    filesToLoad.forEach(file => {
      this.addNewFile(file);
    });

    let needsUpdate = filesToDelete.length > 0;
    tagFiles.forEach(file => {
      const updated = this.updateTagData(file.id, file);
      needsUpdate = needsUpdate || updated;
    });

    if (needsUpdate) {
      this.sceneViewController.update(this.files);
      this.aerialController.update(this.files);
    }

    this.viewer.zoomToSceneExtent(this.combinedBoundingBox, false);

    return filesToLoad.length;
  }

  boundingBoxForTags(tags: TagItem[]) {
    const buffer = 2.0;

    const boundingBoxes = tags.map(tag => {
      if (tag.isAerial) return null;

      const position = tag.position.viewer;
      const min = new Vector3().copy(position).subScalar(buffer);
      const max = new Vector3().copy(position).addScalar(buffer);
      return {min, max};
    });

    return mergeBoundingBoxes(boundingBoxes);
  }

  update() {
    this.sceneViewController.updateUniforms();
  }
}

class SceneViewTags {
  public scene: Scene;
  private tagController: TagController;

  private readonly corners: (0 | 1 | 2 | 3)[] = [0, 2, 1, 2, 3, 1];
  private readonly uvs: (0 | 1)[] = [0, 1, 0, 0, 1, 1, 0, 0, 1, 0, 1, 1];
  private readonly vertices: Vector3[] = [
    new Vector3(-0.5, 0.5, 0),
    new Vector3(0.5, 0.5, 0),
    new Vector3(-0.5, -0.5, 0),
    new Vector3(0.5, -0.5, 0)
  ]
  private readonly numberOfVertices = 6;
  private pickingMeshes = [];
  private standardMeshes = [];
  private selectedID = null;
  private maxDistance = 0;

  constructor(tagController: TagController) {
    this.tagController = tagController;

    this.initScene();
  }

  get viewer() {
    return this.tagController.viewer;
  }

  get meshes() {
    return [...this.pickingMeshes, ...this.standardMeshes];
  }

  get materials() {
    return this.meshes.map(mesh => mesh.material);
  }

  get numberOfTags() {
    let count = 0;

    this.standardMeshes.forEach(mesh => {
      let geometry = mesh.geometry;
      let positions = geometry.attributes.position;
      count += (positions.count / this.numberOfVertices);
    });

    return count;
  }

  get combinedBoundingBox() {
    return this.tagController.combinedBoundingBox;
  }

  private get picker(): GPUPicker {
    return this.viewer.gpuPickers.tags;
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  public update(files: TagFile[]) {
    this.scene.clear();
    this.picker.clear();

    this.standardMeshes = [];
    this.pickingMeshes = [];

    files.forEach(file => {
      const sceneViewTags = file.tags.filter(tag => !tag.isAerial);
      if (sceneViewTags.length === 0) return;

      const size = sceneViewTags.length * this.numberOfVertices;
      const positions = new Float32Array(size * 3);
      const visibilities = new Float32Array(size);
      const selected = new Float32Array(size);
      const gpuIndices = new Int32Array(size);
      const uvs = new Float32Array(size * 2);

      sceneViewTags.forEach((tag, i) => {
        const coordinate = tag.position.viewer;

        this.corners.forEach((corner, cornerNumber) => {
          const tagIndex = ((this.corners.length * i) + cornerNumber) * 3;
          const points = this.vertices[corner];

          positions[tagIndex] = points.x + coordinate.x;
          positions[tagIndex + 1] = points.y + coordinate.y;
          positions[tagIndex + 2] = points.z + coordinate.z;
        });

        this.uvs.forEach((uv, j) => {
          uvs[(this.uvs.length * i) + j] = uv;
        });

        const start = i * this.numberOfVertices;

        for (let j = start; j < start + this.numberOfVertices; j++) {
          visibilities[j] = +tag.visible;
          selected[j] = 0;
          gpuIndices[j] = 0;
        }
      });

      const geometry = new BufferGeometry();

      geometry.setAttribute('position', new BufferAttribute(positions, 3));
      geometry.setAttribute('visible', new BufferAttribute(visibilities, 1));
      geometry.setAttribute('selected', new BufferAttribute(selected, 1));
      geometry.setAttribute('gpu_index', new BufferAttribute(gpuIndices, 1));
      geometry.setAttribute('uv', new BufferAttribute(uvs, 2));

      const tagMaterial = new TagMaterial(file.texture, file.size, false);
      const standardMesh = new TagMesh(geometry, tagMaterial, sceneViewTags);
      this.scene.add(standardMesh);

      const pickingMaterial = new TagMaterial(file.texture, file.size, true);
      const pickingMesh = new TagMesh(geometry, pickingMaterial, sceneViewTags);
      this.picker.add(pickingMesh);

      this.standardMeshes.push(standardMesh);
      this.pickingMeshes.push(pickingMesh);
    });

    this.updatePicker();
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  setMaxDistance(value: number) {
    this.maxDistance = value;
    this.updateUniforms();
  };

  public updateUniforms() {
    if (this.numberOfTags === 0) return;

    const camera = this.viewer.camera;
    if (!camera) return;

    const position = camera.position;
    const rotation = camera.rotation.toVector3();
    const initialCameraLoading = this.viewer.initialCameraLoading;

    const controls = this.viewer.controls;
    const target = controls.getCameraTarget();

    this.materials.forEach((material: TagMaterial) => {
      material.currentCameraRotation = rotation;
      material.currentCameraPosition = position;
      material.currentCameraTarget = target;
      material.windowHeight = window.innerHeight;
      material.initialCameraLoading = initialCameraLoading;
      material.fov = MathUtils.degToRad(camera.fov);
      material.distance = this.maxDistance;
      material.needsUpdate = true;
    });
  }

  tagFromPickerIndex(mesh, index) {
    if (!mesh) {
      return null;
    }

    const attributes = mesh.geometry.attributes;
    const arrayIndex = attributes.gpu_index.array.indexOf(index);
    const tagIndex = Math.floor(arrayIndex/this.numberOfVertices);

    return mesh.tags[tagIndex];
  }

  getFeatureForEvent(event): TagItem[] {
    this.picker.update();
    const index = this.picker.value(event);
    const mesh = this.picker.getSelectedObject(index);
    const selected = this.tagFromPickerIndex(mesh, index);
    this.markTagSelected(selected, index);
    return selected ? [selected] : [];
  }

  markTagSelected(selected, index) {
    this.meshes.forEach(mesh => {
      const attributes = mesh.geometry.attributes;
      attributes.selected.array.fill(0.0);

      const arrayIndex = attributes.gpu_index.array.indexOf(index);
      const selectedIndex = Math.floor(arrayIndex/this.numberOfVertices);
      const baseIndex = selectedIndex*this.numberOfVertices;

      if (arrayIndex !== -1) {
        for (let i = 0; i < this.numberOfVertices; i++) {
          attributes.selected.array[baseIndex + i] = 1.0;
        }
      }
      attributes.selected.needsUpdate = true;
    });

    const newSelectedID = selected?.id;
    if (newSelectedID !== this.selectedID) {
      this.updatePicker();
    }

    this.selectedID = newSelectedID;
  }

  public onMouseUp(event) {
    if (event.mouseMoved) return false;

    if (event.isLeftClick) {
      const selected = this.getFeatureForEvent(event);

      if (selected.length > 0) {
        const selectedTagID = selected[0].id;
        showTagDetails(selectedTagID, false);
      }
    }

    return false;
  }

  public onMouseMove(event) {
    if (this.numberOfTags === 0) return;

    const selected = this.getFeatureForEvent(event);

    if (selected.length > 0) {
      const selectedTagID = selected[0].id;
      let tooltipText = this.tagController
        .getTooltipText(selectedTagID);

      setViewerTooltip(tooltipText);
    } else {
      clearViewerTooltip();
    }
  }
}

class AerialTags {
  private tagController: TagController;
  private readonly source: VectorSource<any>;
  private readonly layer: VectorLayer<any>;
  private styleCache = {};
  public hovering = false;

  constructor(tagController: TagController) {
    this.tagController = tagController;
    this.source = new VectorSource();

    this.layer = new VectorLayer({
      zIndex: 1,
      source: new Cluster({
        source: this.source,
      }),
      style: feature => {
        return this.getClusterStyle(feature);
      },
    });

    this.map.addLayer(this.layer);
  }

  get map(): OLMap {
    const viewer = this.tagController.viewer;
    return viewer.minimap.map;
  }

  get numberOfTags() {
    return this.source.getFeatures().length;
  }

  public onMouseMove(event) {
    if (this.numberOfTags === 0) return;

    const features = this.getFeatureForEvent(event);
    this.hovering = features.length > 0;

    if (features.length === 0) {
      clearAerialTooltip();
    } else if (features.length === 1) {
      const selected = features[0];
      let tooltipText = this.tagController
        .getTooltipText(selected.tagID);

      setAerialTooltip(tooltipText);
    } else {
      setAerialTooltip([
        t("tags.multiple-tags", {
          count: features.length
        })
      ]);
    }
  }

  getFeatureForEvent(event): TagFeature[] {
    return this.map.getFeaturesAtPixel(event.pixel)
      .filter(feature => feature.get('features'))
      .reduce((previous, current) => [...previous, ...current.get('features')], [])
      .filter(feature =>  feature instanceof TagFeature);
  }

  getClusterStyle(feature) {
    const features = feature.get('features');
    const size = features.length;

    let tagScale = 0;
    const texturePaths = {};
    features.forEach((feature: TagFeature) => {
      const texture = feature.texture;
      const texturePath = getTagTexturePath(texture);
      tagScale = Math.max(tagScale, feature.size);

      if (!(texturePath in texturePaths)) {
        texturePaths[texturePath] = 0;
      }
      texturePaths[texturePath] += 1;
    });

    const mostFrequent = Object.keys(texturePaths)
      .sort((a,b) => texturePaths[a] - texturePaths[b])
      .reverse()
      .slice(0, 3);

    // Unique attributes that determines if we need a new style
    const attributes = [size, ...mostFrequent, tagScale];
    const key = attributes.join("-");

    let style = this.styleCache[key];

    if (!style) {
      const strokeColor = '#000000';
      const textColor = '#FFFFFF';
      const combinedStyles = [];
      const iconSize = tagIconSize *tagScale;

      // First texture in feature cluster
      combinedStyles.push(new Style({
        image: new Icon({
          src: mostFrequent[0],
          width: iconSize,
          height: iconSize,
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction'
        })
      }));

      // Second texture in feature cluster
      if (mostFrequent[1]) {
        combinedStyles.push(new Style({
          image: new Icon({
            src: mostFrequent[1],
            width: iconSize,
            height: iconSize,
            anchor: [0.5 + 0.2, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        }));
      }

      // Third texture in feature cluster
      if (mostFrequent[2]) {
        combinedStyles.push(new Style({
          image: new Icon({
            src: mostFrequent[2],
            width: iconSize,
            height: iconSize,
            anchor: [0.5 + 0.4, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
          })
        }));
      }

      combinedStyles.reverse();

      combinedStyles.push(new Style({
        text: new Text({
          text: size > 1 ? size.toString() : "",
          offsetY: -15,
          offsetX: 7.5,
          scale: 1.25,
          stroke: new Stroke({
            color: strokeColor,
            width: 3
          }),
          fill: new Fill({
            color: textColor
          }),
        }),
      }));

      style = [...combinedStyles];
      this.styleCache[key] = style;
    }

    return style;
  }

  update(files: TagFile[]) {
    const features = [];

    files.forEach(file => {
      file.tags.forEach(tag => {
        if (!tag.visible) return;

        const point = new Point(tag.position.aerial);
        const feature = new TagFeature(
          tag.id, tag.texture,
          tag.size, point
        );

        features.push(feature);
      });
    });

    this.source.clear();
    this.source.addFeatures(features);
  }
}